import { locationDefaults } from './location-defaults';
import { compileDefinitionInputs, definitionReaders, type DefinitionRow } from './location-type-ambience';
import { fromBaseRecord } from '../compile/from-base-context';
import { readItemUuidSlot } from '../compile/inventory-raw-readers';
import { isUuid } from '../reactions/layer-list-ids';
import { encounterCreateZone } from './encounter-create';
export { encounterCreateZone } from './encounter-create';

/** The create form judges its catalog choices; the save only persists the chosen references. */
export async function encounterCreateCatalogRefusal(environment: string, weather: string) {
    const page = window as unknown as { fetchAndCacheObject?: (id: string) => Promise<unknown> };
    if (!page.fetchAndCacheObject) throw new Error('Encounter catalog unavailable. Please retry.');
    for (const [id, type, message] of [
        [environment, 'environment', 'Pick an environment; the scene cannot render without one.'],
        [weather, 'weather', 'Pick the weather.'],
    ] as const) {
        const row = isUuid(id) ? await page.fetchAndCacheObject(id) : null;
        if (readItemUuidSlot(definitionReaders.item(row, 'item_id')).toUpperCase() !== id.toUpperCase()
            || definitionReaders.item(row, 'item_type') !== type || Number(definitionReaders.item(row, 'item_deleted') ?? 0) !== 0)
            return { error: `${type}_required`, message };
    }
    return null;
}

/** One projection for the editor, create form and current-location ambience action. */
export async function encounterDefaults(raw: unknown, masterParent: string, locationId: string) {
    if (!fromBaseRecord(raw) || raw.encounter_master_parent_id !== masterParent || raw.encounter_location_id !== locationId
        || !fromBaseRecord(raw.location) || !Array.isArray(raw.audio) || !raw.audio.every(fromBaseRecord)
        || readItemUuidSlot(definitionReaders.layer(raw.location, 'location_id')).toUpperCase() !== locationId.toUpperCase())
        throw new Error('Encounter defaults inputs incomplete. Please retry.');
    const typeId = readItemUuidSlot(definitionReaders.layer(raw.location, 'location_type'));
    const definitions = await compileDefinitionInputs(raw, typeId ? [typeId] : []);
    const type = definitions.get(typeId.toUpperCase()) ?? null;
    if (typeId && !type) throw new Error('Location type unavailable. Please retry.');
    const defaults = locationDefaults(type, fromBaseRecord(raw.parent) ? raw.parent : null);
    const audio: DefinitionRow[] = raw.audio;
    return { ...defaults, zone: 'sibling_zones' in raw ? encounterCreateZone(raw, masterParent, locationId) : raw.default_encounter_zone_number ?? null,
        temperature: readItemUuidSlot(definitionReaders.layer(raw.location, 'location_temperature')) || null,
        wind: readItemUuidSlot(definitionReaders.layer(raw.location, 'location_wind')) || null,
        ambience: defaults.ambience.map(slot => {
            const row = audio.find(row => String(definitionReaders.item({ items: row }, 'audio_id') ?? '').toUpperCase() === slot.id.toUpperCase());
            return { prefix: slot.control.replace('location_', 'encounter_').replace(/_id$/, ''), id: slot.id,
                cat: row ? definitionReaders.item({ items: row }, 'audio_category') ?? null : null,
                sub: row ? definitionReaders.item({ items: row }, 'audio_subcategory') ?? null : null };
        }) };
}

export type EncounterDefaults = Awaited<ReturnType<typeof encounterDefaults>>;

/** Fixed create-command columns; form controls override the editable basics at submit. */
export function encounterDefaultSaveFields(defaults: EncounterDefaults) {
    return { encounter_tempurature: defaults.temperature, encounter_wind: defaults.wind,
        encounter_amb: defaults.ambience[0]?.id || null, encounter_amb_night: defaults.ambience[1]?.id || null,
        encounter_amb_weather: defaults.ambience[2]?.id || null };
}
